export const MATCH_RETRY_MS = Number(process.env.REACT_APP_ONDEMAND_MATCH_RETRY_MS || 5000);

const LANGUAGE_NAME_TO_CODE = {
  english: 'en',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  italian: 'it',
  portuguese: 'pt',
  dutch: 'nl',
  russian: 'ru',
  chinese: 'zh',
  mandarin: 'zh',
  japanese: 'ja',
  korean: 'ko',
  arabic: 'ar',
  hindi: 'hi',
  bengali: 'bn',
  urdu: 'ur',
  turkish: 'tr',
  polish: 'pl',
  swedish: 'sv',
  norwegian: 'no',
  danish: 'da',
  finnish: 'fi',
  greek: 'el',
  hebrew: 'he',
  thai: 'th',
  vietnamese: 'vi',
  indonesian: 'id',
  malay: 'ms',
};

const COUNTRY_NAME_TO_CODE = {
  "united states": "US",
  "united states of america": "US",
  usa: "US",
  us: "US",
  canada: "CA",
  australia: "AU",
  "united kingdom": "GB",
  uk: "GB",
  india: "IN",
};

const normalizeLanguageCode = (value) => {
  if (!value) return value;
  const lower = String(value).trim().toLowerCase();
  return LANGUAGE_NAME_TO_CODE[lower] || lower;
};

const normalizeCountryCode = (value) => {
  if (!value) return value;
  const trimmed = String(value).trim();
  const lower = trimmed.toLowerCase();
  return COUNTRY_NAME_TO_CODE[lower] || trimmed;
};

const getFirstValue = (value) => {
  if (Array.isArray(value) && value.length) {
    return getFirstValue(value[0]);
  }

  if (value && typeof value === "object") {
    return (
      value.code ||
      value.short_name ||
      value.country_code ||
      value["country_code "] ||
      value.country_name ||
      value.language_name ||
      value.lanugage_name ||
      value.name ||
      value.label ||
      value.value
    );
  }

  return value;
};

const sanitizeMatchingValue = (value, fallback) => {
  const sanitized = String(value || fallback || "").trim();
  return sanitized.length >= 2 ? sanitized : fallback;
};

export const getAppointmentId = (matchData) => {
  if (!matchData) return "";

  return matchData.appointmentId || matchData.appointment_id || matchData.id || "";
};

export const formatAppointmentNumber = (matchData) => {
  const rawNumber =
    (matchData &&
      (matchData.appointmentNumber ||
        matchData.appointment_number ||
        matchData.frontAppointmentId ||
        matchData.front_appointment_id)) ||
    getAppointmentId(matchData);

  if (!rawNumber) return "";

  const normalized = String(rawNumber).trim();
  const match = normalized.match(/^2A(.+)X$/i);
  const appointmentId = match ? match[1] : normalized;

  return appointmentId ? `2A${appointmentId}X` : "";
};

export const getAppointmentSlot = (matchData) => {
  if (!matchData) return null;

  const date =
    matchData.date ||
    matchData.appointmentDate ||
    matchData.appointment_date ||
    "";
  const time =
    matchData.time ||
    matchData.appointmentTime ||
    matchData.appointment_time ||
    "";
  const timezone =
    matchData.timezone ||
    matchData.clientTimezone ||
    matchData.client_timezone ||
    "";

  if (!date || !time) {
    return null;
  }

  return { date, time, timezone };
};

export const formatAppointmentSlotLabel = (matchData) => {
  const slot = getAppointmentSlot(matchData);

  if (!slot) {
    return "";
  }

  const [year, month, day] = String(slot.date)
    .split("-")
    .map((value) => Number.parseInt(value, 10));
  const [hourValue = "0", minuteValue = "00"] = String(slot.time).split(":");
  const hour = Number.parseInt(hourValue, 10);
  const minute = Number.parseInt(minuteValue, 10);
  const hasValidDate = [year, month, day].every((value) => Number.isInteger(value));
  const hasValidTime = Number.isInteger(hour) && Number.isInteger(minute);

  const formattedDate = hasValidDate
    ? new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(Date.UTC(year, month - 1, day)))
    : slot.date;

  const normalizedHour = hasValidTime ? hour : 0;
  const normalizedMinute = hasValidTime ? minute : 0;
  const period = normalizedHour >= 12 ? "PM" : "AM";
  const displayHour = normalizedHour % 12 || 12;
  const formattedTime = `${displayHour}:${String(normalizedMinute).padStart(2, "0")} ${period}`;
  const formattedTimezone = slot.timezone ? ` (${slot.timezone})` : "";

  return `${formattedDate} at ${formattedTime}${formattedTimezone}`;
};

export const buildMatchingCriteria = (user) => {
  const fallbackCountry = process.env.REACT_APP_ONDEMAND_MATCH_COUNTRY || "US";
  const fallbackLanguage = process.env.REACT_APP_ONDEMAND_MATCH_LANGUAGE || "en";
  const profileCountry =
    user &&
    (user.country_code ||
      user.country ||
      user.country_name ||
      getFirstValue(user.countries));
  const profileLanguage =
    user &&
    (user.language ||
      user.language_name ||
      getFirstValue(user.languages) ||
      getFirstValue(user.medium_languages));

  return {
    country: sanitizeMatchingValue(normalizeCountryCode(profileCountry), fallbackCountry),
    language: sanitizeMatchingValue(normalizeLanguageCode(profileLanguage), fallbackLanguage),
  };
};

export const getMatchLabel = (matchData, currentUserId) => {
  if (!matchData) return "";

  const clientId = matchData.clientId || matchData.client_id;
  const providerId = matchData.providerId || matchData.mediumId || matchData.medium_id;
  const clientName =
    matchData.clientName ||
    matchData.client_name ||
    matchData.userScreenName ||
    matchData.user_screen_name;
  const providerName =
    matchData.providerName ||
    matchData.provider_name ||
    matchData.mediumName ||
    matchData.medium_name ||
    matchData.mediumScreenName ||
    matchData.medium_screen_name;
  const currentId = currentUserId ? String(currentUserId) : "";

  if (currentId && String(clientId) === currentId) {
    return providerName || (providerId ? `Medium #${providerId}` : "your medium");
  }

  if (currentId && String(providerId) === currentId) {
    return clientName || (clientId ? `Client #${clientId}` : "your client");
  }

  return providerName || clientName || (providerId ? `Medium #${providerId}` : "your match");
};
